import React, { useEffect } from "react"
import { useDispatch } from "react-redux"
import { useTranslation } from "react-i18next"
import { useForm, Controller } from "react-hook-form"
import { zodResolver } from "@hookform/resolvers/zod"
import theme from "lib/material-ui/theme"
import { styled, Theme } from "@mui/material/styles"
import Stack from "@mui/material/Stack"
import Typography from "@mui/material/Typography"
import { debounce } from "@mui/material"
import { wishListSchema } from "validations/wishList"
import { useAppSelector } from "appRedux/hooks"
import { updateListFormFields } from "appRedux/slices/listFormSlice"
import { CLOSE_FRIENDS, DEBOUNCE_WAIT } from "constants/index"
import { privacyOptions } from "constants/options"
import { StyledLabel } from "components/atoms/FormField"
import TextField from "components/atoms/TextField"
import CTA from "components/atoms/CTA"
import CloseFriendsList from "components/organisms/CloseFriendsList"
import withPremiumPermission from "components/HOCs/withPremiumPermission"

type WishListFormProps = {
  isCTADisabled: (dirtyFields: object) => boolean;
  isCTALoading: () => boolean;
  onSubmit: (value: WishListRequestPayload) => void;
}

export default function WishListForm(props: WishListFormProps) {
  const { isCTADisabled, isCTALoading, onSubmit } = props

  const formFields = useAppSelector((state) => state.listForm.formFields)
  const prefilledFormFields = useAppSelector((state) => state.listForm.prefilledFormFields)
  const isPrefilled = useAppSelector((state) => state.listForm.isPrefilled)

  const { t } = useTranslation()
  const dispatch = useDispatch()

  const {
    control,
    register,
    setValue,
    getValues,
    handleSubmit,
    watch,
    reset,
    formState: { errors, dirtyFields, isDirty }
  } = useForm<WishListRequestPayload>({
    resolver: zodResolver(wishListSchema),
    shouldFocusError: false,
    reValidateMode: "onBlur",
    defaultValues: formFields
  })

  const privacy = watch("privacy")

  useEffect(() => {
    if (isPrefilled || JSON.stringify(formFields) === JSON.stringify(getValues())) return
    reset(formFields)
  }, [formFields])

  useEffect(() => {
    if (!isPrefilled) return

    setTimeout(() => {
      setValue("name", prefilledFormFields.name, { shouldDirty: true })
      setValue("privacy", prefilledFormFields.privacy, { shouldDirty: true })
      setFormValuesToRedux(prefilledFormFields)
    }, 1)
  }, [])

  function setPrivacy(value: Privacy) {
    setValue("privacy", value, { shouldDirty: true })
    setFormValuesToRedux({
      ...formFields,
      privacy: value
    })
  }

  const handleOnFormBlur = debounce(function () {
    const formFieldsValue = getValues()
    setFormValuesToRedux(formFieldsValue)
  }, DEBOUNCE_WAIT)

  function setFormValuesToRedux(values: WishListRequestPayload) {
    const formFieldsValue = getValues()
    dispatch(updateListFormFields(formFieldsValue))
  }

  return (
    <Form onSubmit={handleSubmit(onSubmit)} onChange={handleOnFormBlur}>
      <Stack height="100%" flexGrow={1}>
        <TextField
          id="name"
          label={t("collections.listName")}
          defaultValue={getValues("name")}
          register={register}
          setValue={setValue}
          error={errors.name}
        />

        <PrivacySelectionContainer>
          <StyledLabel>
            {t("collections.showListTo")}
          </StyledLabel>

          <Controller
            name="privacy"
            control={control}
            render={() =>
              <PrivacyOptionsContainer>
                {privacyOptions.map(item => (
                  <PrivacyOptionWithPremiumPermission
                    key={item.label}
                    feature={item.key}
                    type={item.key}
                    isselected={getValues("privacy") === item.key ? 1 : 0}
                    color={item.getColor(theme)}
                    onClick={() => setPrivacy(item.key as Privacy)}
                  >
                    {getValues("privacy") === item.key ? item.filledIcon : item.icon}
                    <label>{t(item.label)}</label>
                  </PrivacyOptionWithPremiumPermission>
                ))}
              </PrivacyOptionsContainer>
            }
          />

          <Description>
            {t(`collections.${privacy.toLowerCase()}_description`)}
          </Description>

          {getValues("privacy") === CLOSE_FRIENDS && <CloseFriendsList/>}
        </PrivacySelectionContainer>
      </Stack>

      <ButtonsWrapper>
        <CTA
          variant="contained"
          type="submit"
          disabled={isCTADisabled(dirtyFields)}
          loading={isCTALoading()}
          onClick={handleSubmit(onSubmit)}
        >
          {t("general.save")}
        </CTA>

      </ButtonsWrapper>
    </Form>
  )
}

const Form = styled("form")(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  justifyContent: "space-between",
  flexGrow: 1,
  marginTop: "24px"
}))

const ButtonsWrapper = styled(Stack)(({ theme }) => ({
  width: "100%",
  flexDirection: "row",
  alignItems: "center",
  gap: "24px"
}))

const PrivacySelectionContainer = styled(Stack)(({
  height: "100%",
  flexGrow: 1
}))

const PrivacyOptionsContainer = styled(Stack)(({
  flexDirection: "row",
  justifyContent: "space-between",
  gap: "8px"
}))

type PrivacyOptionProps = {
  type: string;
  theme?: Theme;
  isselected: 1 | 0;
  color: string | undefined;
}

const PrivacyOption = styled(Stack)(({ theme, isselected, color }: PrivacyOptionProps) => ({
  width: "100%",
  justifyContent: "center",
  alignItems: "center",
  gap: "8px",
  color: isselected && theme ? color : theme?.palette?.gray["12"],
  fontSize: "0.95rem",
  transition: "0.2s",
  borderRadius: "8px",
  padding: "8px",
  paddingTop: "16px",

  "& svg": {
    transition: "0.1s"
  }
}))

const Description = styled(Typography)(({ theme }) => ({
  fontSize: "1.1rem",
  color: theme.palette.gray["13"],
  margin: "20px 18px"
}))

const PrivacyOptionWithPremiumPermission = withPremiumPermission(PrivacyOption)
